<template>
    <page :tabs="tabs" :busy="busy">
        <grid
            ref="grid"
            :columnDefs="columnDefs"
            :rowData="rowData"
            :statusBar="statusBar"
            grid-theme="ag-theme-alpine-report"
        >
            <date-range-picker
                :range-options="predefinedRanges"
                :value="dateRange"
                :date-format="userUnicodeDateFormat"
                @input="handleDaterangeChange"
            ></date-range-picker>
            <responsive-dropdown
                :icon="['fas', 'filter']"
                :items="showingTypeOptions"
                variant="primary"
                >{{ showTitle }}
            </responsive-dropdown>
            <responsive-button
                :href="exportExcelHref"
                @click="generatingExcelReportSuccessToast"
                :icon="['fas', 'file-excel']"
                variant="primary"
            ></responsive-button>

            <exportToPDFButton
                :href="exportToPDFHref"
                :pdfCacheKey="pdfCacheKey"
                :pageOrientation="pageOrientation"
            />
            <responsive-dropdown
                :icon="['fas', 'file']"
                :items="orientationFileOption"
                variant="primary"
                >{{ orientationFileLabel }}
            </responsive-dropdown>
            <report-settings-modal
                :modalId="reportSettingsModalId"
                :initialValues="reportSettings"
            />
        </grid>
    </page>
</template>

<script>
import { mapGetters } from 'vuex';
import format from 'date-fns/format';
import API from '../../../api';
import Page from '../../generic/page/page.vue';
import Grid from '../../generic/grid/grid.vue';
import DateRangePicker, {
    nameToRange,
    TODAY,
    YESTERDAY,
    THIS_MONTH,
    LAST_MONTH,
    ALL_DATES,
    TOMORROW,
    THIS_WEEK,
    NEXT_WEEK,
    NEXT_MONTH,
    THIS_YEAR,
    LAST_WEEK,
} from '../../generic/date-range-picker/date-range-picker.vue';
import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
import ResponsiveDropdown from '../../generic/responsive-dropdown/responsive-dropdown.vue';
import totalStatusBar from './total-status-bar.vue';
import { REPORT_CURRENT_GUEST_ENDPOINT } from '../../../endpoints';
import ReportSettingsModal, {
    DATE_PERIODS_INPUT,
    SELECT_INPUT,
} from '../report-settings/report-settings-modal.vue';
import { defineAsyncComponent } from 'vue';

const exportToPDFButton = defineAsyncComponent(() =>
    import(
        /* webpackChunkName: "lx-export-to-pdf-button" */
        '../../generic/export-to-pdf-button/export-to-pdf-button.vue'
    ))

const SHOWING_TYPE_WITH_LAST_DAY = true;
const SHOWING_TYPE_WITHOUT_LAST_DAY = false;

export default {
    name: 'ReportCurrentGuests',
    components: {
        Page,
        Grid,
        DateRangePicker,
        ResponsiveButton,
        ResponsiveDropdown,
        exportToPDFButton,
        ReportSettingsModal,
        totalStatusBar: totalStatusBar,
    },

    data() {
        return {
            busy: null,
            dateRange: nameToRange(TODAY),
            dateRequestFormat: 'yyyy-MM-dd',
            landscapeOrientationFileLabel: 'Landscape',
            portraitOrientationFileLabel: 'Portrait',
            orientationFileLabel: 'Landscape',
            pageOrientation: 'landscape',
            predefinedRanges: [
                YESTERDAY,
                TODAY,
                TOMORROW,
                LAST_WEEK,
                THIS_WEEK,
                NEXT_WEEK,
                LAST_MONTH,
                THIS_MONTH,
                NEXT_MONTH,
                THIS_YEAR,
                ALL_DATES,
            ],
            rowData: [],
            showDepartures: true,
            tabs: [
                {
                    title: 'Current Guests',
                    route: '/app/reports/current-guests/',
                    active: true,
                },
            ],
            total: 0,
            pdfCacheKey: null,
            reportSettingsModalId: 'report-settings-modal',
        };
    },
    computed: {
        ...mapGetters('header', [
            'userUnicodeDateFormat',
            'userCurrencySymbol',
        ]),
        columnDefs() {
            return [
                {
                    field: 'name',
                    headerName: 'Guest Name',
                    minWidth: 200,
                    cellRenderer: ({ data }) => {
                        return `${data.name.replace('&amp;', '&')}</a>`;
                    },
                },
                {
                    field: 'email',
                    headerName: 'Email',
                    minWidth: 280,
                },
                {
                    field: 'phone',
                    headerName: 'Phone',
                    minWidth: 170,
                },
                {
                    field: 'arrival',
                    headerName: 'Arrival',
                    minWidth: 190,
                    type: 'dateTimeColumn',
                },
                {
                    field: 'departure',
                    headerName: 'Departure',
                    minWidth: 190,
                    type: 'dateTimeColumn',
                },
                {
                    field: 'property',
                    headerName: 'Property',
                    minWidth: 230,
                },
                {
                    field: 'unit',
                    headerName: 'Unit #',
                    minWidth: 140,
                },
                {
                    field: 'invoice',
                    headerName: 'Invoice #',
                    minWidth: 120,
                    cellRenderer: ({ data }) => {
                        return `<a href="/app/reservation/edit/invoice/${data.invoice}">${data.invoice}</a>`;
                    },
                },
                {
                    field: '',
                    headerName: 'Total Guests',
                    minWidth: 120,
                    valueGetter: ({ data }) => {
                        return `${data.adults}/${data.children}/${data.guests}`;
                    },
                },
            ];
        },
        showingTypeOptions() {
            return [
                {
                    label: this.showDeparturesTitle,
                    click: () => {
                        this.showDepartures = SHOWING_TYPE_WITH_LAST_DAY;
                        this.fetchData();
                    },
                },
                {
                    label: this.hideDeparturesTitle,
                    click: () => {
                        this.showDepartures = SHOWING_TYPE_WITHOUT_LAST_DAY;
                        this.fetchData();
                    },
                },
            ];
        },
        orientationFileOption() {
            return [
                {
                    label: this.landscapeOrientationFileLabel,
                    click: () => {
                        this.pageOrientation = 'landscape';
                        this.orientationFileLabel =
                            this.landscapeOrientationFileLabel;
                    },
                },
                {
                    label: this.portraitOrientationFileLabel,
                    click: () => {
                        this.pageOrientation = 'portrait';
                        this.orientationFileLabel =
                            this.portraitOrientationFileLabel;
                    },
                },
            ];
        },
        endDateRequest() {
            return format(this.dateRange.to, this.dateRequestFormat);
        },
        exportExcelHref() {
            return `/app/reports/current-guests/row-data/excel/?from_date=${this.startDateRequest}&to_date=${this.endDateRequest}&show_departures=${this.showDepartures}`;
        },
        startDateRequest() {
            return format(this.dateRange.from, this.dateRequestFormat);
        },
        startDateFormatted() {
            return format(this.dateRange.from, this.userUnicodeDateFormat);
        },
        statusBar() {
            return {
                statusPanels: [
                    {
                        statusPanel: 'totalStatusBar',
                        align: 'right',
                        statusPanelParams: { context: this.total },
                    },
                ],
            };
        },
        exportToPDFHref() {
            return `/app/reports/current-guests/row-data/pdf/?from_date=${this.startDateRequest}&to_date=${this.endDateRequest}&show_departures=${this.showDepartures}`;
        },
        showTitle() {
            return `${this.showDepartures ? 'Show' : 'Hide'} ${
                this.startDateFormatted
            } Departures`;
        },
        showDeparturesTitle() {
            return `Show ${this.startDateFormatted} Departures`;
        },
        hideDeparturesTitle() {
            return `Hide ${this.startDateFormatted} Departures`;
        },
        reportSettings() {
            return {
                name: 'Current Guests',
                report_sysname: 'CURRENT_GUESTS',
                settings: [
                    {
                        type: DATE_PERIODS_INPUT,
                        value: 'this_month',
                        label: 'Date',
                        name: 'date',
                        predefinedRanges: [
                            YESTERDAY,
                            TODAY,
                            TOMORROW,
                            LAST_WEEK,
                            THIS_WEEK,
                            NEXT_WEEK,
                            LAST_MONTH,
                            THIS_MONTH,
                            NEXT_MONTH,
                            THIS_YEAR,
                        ],
                    },
                    {
                        type: SELECT_INPUT,
                        value: SHOWING_TYPE_WITHOUT_LAST_DAY,
                        label: 'Show Departures',
                        name: 'show_departures',
                        options: [
                            {
                                text: 'Yes',
                                value: SHOWING_TYPE_WITH_LAST_DAY,
                            },
                            {
                                text: 'No',
                                value: SHOWING_TYPE_WITHOUT_LAST_DAY,
                            },
                        ],
                    },
                ],
            };
        },
    },
    mounted() {
        this.fetchData();
    },
    methods: {
        async fetchData() {
            try {
                this.busy = true;
                const { data } = await API.get(
                    REPORT_CURRENT_GUEST_ENDPOINT(),
                    {
                        params: {
                            from_date: this.startDateRequest,
                            to_date: this.endDateRequest,
                            show_departures: this.showDepartures,
                        },
                    }
                );
                this.rowData = data.guests;
                this.total = data.total;
                this.pdfCacheKey = data.pdf_cache_key;
                this.$refs.grid.gridOptions.context = { total: this.total };
            } catch (e) {
                this.errorToast({ text: 'Could not fetch data' });
            } finally {
                this.busy = false;
            }
        },
        handleDaterangeChange(value) {
            this.dateRange = { ...value };
            this.fetchData();
        },
    },
};
</script>
